import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Users, UserPlus, Copy, Check, Trophy, TrendingUp,
  Gift, Link2, Calendar, Star, Award, Share2,
  RefreshCw, Search, Filter, Download
} from 'lucide-react';
import { api } from '@/lib/api';
import { ExportButton } from '@/components/ExportButton';

interface Referral {
  id: number;
  referralCode: string;
  referredName: string | null;
  referredPhone: string | null;
  status: string;
  rewardPoints: number;
  convertedAt: string | null;
  createdAt: string;
}

interface Stats {
  total: number;
  converted: number;
  pending: number;
  totalPointsEarned: number;
  conversionRate: number;
}

interface LeaderboardEntry {
  customerId: number;
  customerName: string;
  totalReferrals: number;
  totalPoints: number;
  convertedCount: number;
}

interface MyCode {
  referralCode: string;
  rewardPoints: number;
  expiresAt: string;
  referralLink: string;
  stats: Stats;
}

export default function ReferralsPage() {
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(40);
  const [myCode, setMyCode] = useState<MyCode | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      loadData();
    }
  }, [selectedCustomerId]);

  const loadCustomers = async () => {
    try {
      const res = await api.get('/api/customers?pageSize=100');
      if (res.data.success) {
        const data = res.data.data;
        setCustomers(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [codeRes, referralsRes, leaderboardRes] = await Promise.all([
        api.get(`/api/referrals/my-code/${selectedCustomerId}`),
        api.get(`/api/referrals/my-referrals/${selectedCustomerId}`),
        api.get('/api/referrals/leaderboard?top=10')
      ]);

      if (codeRes.data.success) setMyCode(codeRes.data.data);
      if (referralsRes.data.success) setReferrals(referralsRes.data.data);
      if (leaderboardRes.data.success) setLeaderboard(leaderboardRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'converted':
        return <Badge className="bg-green-100 text-green-700 border-green-200">تم التحويل</Badge>;
      case 'active':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">نشط</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">قيد الانتظار</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700 border-red-200">منتهي</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredReferrals = referrals.filter(r => 
    !statusFilter || r.status === statusFilter
  );

  const exportColumns = [
    { key: 'referralCode', label: 'كود الإحالة' },
    { key: 'referredName', label: 'المحال' },
    { key: 'status', label: 'الحالة' },
    { key: 'rewardPoints', label: 'النقاط' },
    { key: 'createdAt', label: 'التاريخ', format: (v: string) => new Date(v).toLocaleDateString('ar-EG') }
  ];

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            نظام الإحالات
          </h1>
          <p className="text-muted-foreground mt-1">إدارة وتتبع إحالات العملاء والمكافآت</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(Number(e.target.value))}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            {customers.map(c => (
              <option key={c.id} value={c.id}>{c.fullName || c.name} (#{c.id})</option>
            ))}
          </select>
          <ExportButton data={referrals} filename="referrals" columns={exportColumns} title="الإحالات" />
          <Button onClick={loadData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
      </div>

      {/* My Referral Code Card */}
      {myCode && (
        <Card className="bg-gradient-to-l from-purple-600 via-pink-600 to-red-600 text-white border-0 overflow-hidden relative">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full -translate-x-48 -translate-y-48 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-yellow-400/20 rounded-full translate-x-32 translate-y-32 blur-3xl"></div>
          <CardContent className="p-6 relative">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">كود الإحالة الخاص بك</h3>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-white/70 text-xs mb-1">الكود</p>
                      <p className="text-3xl font-bold font-mono tracking-wider">{myCode.referralCode}</p>
                    </div>
                    <Button
                      onClick={() => copyToClipboard(myCode.referralCode)}
                      className="bg-white text-purple-600 hover:bg-white/90"
                      size="lg"
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      <span className="mr-2">{copied ? 'تم النسخ' : 'نسخ'}</span>
                    </Button>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-4 h-4" />
                    <p className="text-white/70 text-xs">رابط الإحالة</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-mono flex-1 truncate">{myCode.referralLink}</p>
                    <Button
                      onClick={() => copyToClipboard(myCode.referralLink)}
                      size="sm"
                      variant="secondary"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/20 backdrop-blur rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4" />
                    <p className="text-white/70 text-xs">المكافأة</p>
                  </div>
                  <p className="text-2xl font-bold">{myCode.rewardPoints}</p>
                  <p className="text-xs text-white/70">نقطة لكل إحالة</p>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4" />
                    <p className="text-white/70 text-xs">الإجمالي</p>
                  </div>
                  <p className="text-2xl font-bold">{myCode.stats.total}</p>
                  <p className="text-xs text-white/70">إحالة</p>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4" />
                    <p className="text-white/70 text-xs">تم التحويل</p>
                  </div>
                  <p className="text-2xl font-bold">{myCode.stats.converted}</p>
                  <p className="text-xs text-white/70">عميل جديد</p>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <p className="text-white/70 text-xs">معدل التحويل</p>
                  </div>
                  <p className="text-2xl font-bold">{myCode.stats.conversionRate}%</p>
                  <p className="text-xs text-white/70">نسبة النجاح</p>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-2 text-sm text-white/80">
              <Calendar className="w-4 h-4" />
              <span>صالح حتى: {new Date(myCode.expiresAt).toLocaleDateString('ar-EG')}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {myCode && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">إجمالي النقاط</p>
                  <p className="text-2xl font-bold text-blue-900">{myCode.stats.totalPointsEarned}</p>
                </div>
                <Award className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">تم التحويل</p>
                  <p className="text-2xl font-bold text-green-900">{myCode.stats.converted}</p>
                </div>
                <Check className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">قيد الانتظار</p>
                  <p className="text-2xl font-bold text-yellow-900">{myCode.stats.pending}</p>
                </div>
                <Calendar className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">معدل النجاح</p>
                  <p className="text-2xl font-bold text-purple-900">{myCode.stats.conversionRate}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Referrals List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                قائمة الإحالات
              </CardTitle>
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1 border rounded-lg text-sm bg-white"
                >
                  <option value="">كل الحالات</option>
                  <option value="converted">تم التحويل</option>
                  <option value="active">نشط</option>
                  <option value="pending">قيد الانتظار</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p>جاري التحميل...</p>
              </div>
            ) : filteredReferrals.length === 0 ? (
              <div className="text-center py-12">
                <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">لا توجد إحالات حالياً</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredReferrals.map((ref) => (
                  <div key={ref.id} className="p-4 hover:bg-gray-50 transition">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                          {(ref.referredName || '?').charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold truncate">{ref.referredName || ref.referredPhone || 'قيد المراجعة'}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                            <span className="font-mono">{ref.referralCode}</span>
                            <span>•</span>
                            <span>{new Date(ref.createdAt).toLocaleDateString('ar-EG')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(ref.status)}
                        <div className="text-left">
                          <p className="text-xs text-gray-500">النقاط</p>
                          <p className="font-bold text-purple-600">+{ref.rewardPoints}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              أفضل المحيلين
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">لا يوجد محيلين بعد</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((entry, index) => (
                  <div key={entry.customerId} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-600 text-white' :
                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{entry.customerName}</p>
                      <p className="text-xs text-gray-500">{entry.convertedCount} إحالة محولة</p>
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-gray-500">النقاط</p>
                      <p className="font-bold text-purple-600">{entry.totalPoints}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle>كيف يعمل نظام الإحالات؟</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-purple-600">1</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">شارك كودك</h4>
                <p className="text-sm text-gray-600">شارك كود الإحالة الخاص بك مع أصدقائك</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-blue-600">2</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">تسجيل العميل</h4>
                <p className="text-sm text-gray-600">عندما يسجل صديقك باستخدام الكود</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-green-600">3</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">اكتساب النقاط</h4>
                <p className="text-sm text-gray-600">تحصل على 100 نقطة لكل إحالة ناجحة</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-pink-600">4</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">استبدال النقاط</h4>
                <p className="text-sm text-gray-600">استبدل نقاطك بخصومات ومكافآت</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
